import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    Paper,
    TextField,
    IconButton,
    CircularProgress,
    Fab,
    Fade,
    Chip,
    Avatar,
    Tooltip,
    Dialog,
    DialogContent,
    Drawer,
    useMediaQuery
} from '@mui/material';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles'
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// Assuming ChatBotApi is in a services folder, adjust path if needed
import { ChatBotApi } from '../../services/apis';
import React from 'react';


// --- THEME AND STYLING ---
const theme = createTheme({
    palette: {
        mode: 'light',
        primary: { main: '#5c97c3' },
        secondary: { main: '#3f51b5' },
        background: { default: '#f4f6f8', paper: '#ffffff' },
        text: { primary: '#172b4d', secondary: '#6b778c' }
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        body1: { fontSize: '0.95rem', lineHeight: 1.6 },
        h6: { fontWeight: 600 }
    }
});

const MarkdownStyled = styled(Box)({
    '& p': { margin: '0 0 8px 0' },
    '& h1, & h2, & h3': { margin: '16px 0 8px 0', fontWeight: 'bold' },
    '& ul, & ol': { paddingLeft: '20px', margin: '0 0 8px 0' },
    '& li': { marginBottom: '4px' },
    '& a': { color: theme.palette.secondary.main, textDecoration: 'none', fontWeight: 'bold' },
    '& strong': { color: theme.palette.primary.main },
});


// --- INITIAL STATE & DATA ---
const INITIAL_MESSAGE = {
    id: 1,
    text: "Hello! I'm **Aarv**, your friendly loan advisor. I can help you with loan planning, repayment strategies, and more. What's on your mind today?",
    sender: "bot"
};

const MAIN_SUGGESTIONS = [
    { label: "Plan for a Home Loan", value: "Help me plan for a home loan." },
    { label: "Plan Loan Repayment", value: "How can I repay my loan faster?" },
    { label: "Check Loan Eligibility", value: "How do I check my loan eligibility?" },
];

const GUIDED_RESPONSES = {
    "Help me plan for a home loan.": {
        text: "Great! Planning for a home loan is a big step. To give you the best advice, I need to understand your situation. Let's start with the basics. What's your biggest concern right now?",
        options: [
            { label: "My Credit Score", value: "Tell me all about how credit scores affect home loans." },
            { label: "The Down Payment", value: "How much down payment do I really need for a home loan?" },
            { label: "Different Loan Types", value: "Explain the different types of home loans available." },
            { label: "Start Over", value: "Start Over" },
        ]
    }
};

// --- Child Component: ChatWindow ---
const ChatWindow = ({
    messages,
    isLoading,
    suggestions,
    userInput,
    handleInputChange,
    handleKeyPress,
    handleSendMessage,
    handleResetChat,
    handleCloseChat, // Correctly accept the prop
    isMobile,
    isMaximized,
    setIsMaximized,
    chatContainerRef
}) => (
    <Paper
        elevation={0}
        sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: isMaximized && !isMobile ? '16px' : 0,
            overflow: 'hidden',
            bgcolor: 'background.default'
        }}
    >
        <AppBar position="static" elevation={1} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid #ddd' }}>
            <Toolbar>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 1.5 }}>
                    <AutoAwesomeIcon fontSize="small" sx={{ color: 'white' }} />
                </Avatar>
                <Typography variant="h6" sx={{ flexGrow: 1, color: 'text.primary' }}>
                    Your Loan Advisor
                </Typography>

                {/* --- ICONS --- */}
                <Tooltip title="Reset Chat">
                    <IconButton onClick={handleResetChat}>
                        <RestartAltIcon />
                    </IconButton>
                </Tooltip>

                {/* Maximize button for Desktop */}
                {!isMobile && (
                    <Tooltip title={isMaximized ? "Minimize" : "Maximize"}>
                        <IconButton onClick={() => setIsMaximized(!isMaximized)}>
                            <OpenInFullIcon />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Close button for Mobile */}
                {isMobile && (
                    <Tooltip title="Close">
                        <IconButton onClick={handleCloseChat}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Toolbar>
        </AppBar>

        <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
            {messages.map((message) => (
                <Box key={message.id} sx={{ display: 'flex', justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start', mb: 2, flexShrink: 0 }}>
                    <Paper elevation={0} sx={{ p: 1.5, maxWidth: '85%', bgcolor: message.sender === 'user' ? 'primary.main' : 'background.paper', color: message.sender === 'user' ? 'white' : 'text.primary', borderRadius: message.sender === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px', border: message.sender === 'bot' ? '1px solid #e0e0e0' : 'none' }}>
                        <MarkdownStyled><ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown></MarkdownStyled>
                    </Paper>
                </Box>
            ))}
            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                    <Paper elevation={0} sx={{ p: 1.5, borderRadius: '20px 20px 20px 4px', border: '1px solid #e0e0e0', bgcolor: 'background.paper' }}>
                        <CircularProgress size={22} />
                    </Paper>
                </Box>
            )}
        </Box>

        <Box component="footer" sx={{ p: 1, bgcolor: 'background.paper', borderTop: '1px solid #ddd' }}>
            {suggestions.length > 0 && !isLoading && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1 }}>
                    {suggestions.map(chip => (
                        <Chip key={chip.label} label={chip.label} onClick={() => handleSendMessage(chip.value)} variant="outlined" sx={{ cursor: 'pointer' }} />
                    ))}
                </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField fullWidth variant="outlined" size="small" value={userInput} onChange={handleInputChange} onKeyPress={handleKeyPress} placeholder="Ask a question or select an option..." disabled={isLoading} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '20px' } }} />
                <IconButton color="primary" onClick={() => handleSendMessage(userInput)} disabled={isLoading || userInput.trim() === ''} sx={{ ml: 1 }}><SendIcon /></IconButton>
            </Box>
        </Box>
    </Paper>
);


// --- MAIN CHATBOT COMPONENT ---
export default function Chatbot() {
    const [isChatOpen, setIsChatOpen] = React.useState(false);
    const [isMaximized, setIsMaximized] = React.useState(false);
    const [messages, setMessages] = React.useState([INITIAL_MESSAGE]);
    const [suggestions, setSuggestions] = React.useState(MAIN_SUGGESTIONS);
    const [userInput, setUserInput] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const chatContainerRef = React.useRef(null);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    React.useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages, isLoading]);

    const handleResetChat = () => {
        setMessages([INITIAL_MESSAGE]);
        setSuggestions(MAIN_SUGGESTIONS);
    };

    const getBotResponse = async (prompt) => {
        setIsLoading(true);
        setSuggestions([]);

        const guidedResponse = GUIDED_RESPONSES[prompt];
        if (guidedResponse) {
            setTimeout(() => {
                setMessages(prev => [...prev, { id: Date.now(), text: guidedResponse.text, sender: "bot" }]);
                setSuggestions(guidedResponse.options);
                setIsLoading(false);
            }, 500);
            return;
        }

        try {
            const history = messages.map(msg => ({ sender: msg.sender, text: msg.text.replace(/\*\*([^*]+)\*\*/g, '$1') }));
            const body = JSON.stringify({ prompt, history });
            const response = await ChatBotApi(body);
            const botText = response.reply || "Sorry, I'm having a little trouble right now. Please try again.";
            setMessages(prev => [...prev, { id: Date.now(), text: botText, sender: "bot" }]);
            setSuggestions([{ label: "Main Menu", value: "Start Over" }]);
        } catch (error) {
            console.error("Error fetching bot response:", error);
            const errorText = "I seem to be having trouble connecting. Please check your connection or try again later. For immediate help, you can call our experts at **9899815689**.";
            setMessages(prev => [...prev, { id: Date.now(), text: errorText, sender: "bot" }]);
            setSuggestions([{ label: "Try Again", value: prompt }, { label: "Main Menu", value: "Start Over" }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendMessage = (text) => {
        const messageText = text.trim();
        if (messageText === '' || isLoading) return;
        if (messageText === "Start Over") {
            handleResetChat();
            return;
        }
        setMessages(prev => [...prev, { id: Date.now(), text: messageText, sender: "user" }]);
        getBotResponse(messageText);
        setUserInput('');
    };

    const handleInputChange = (e) => setUserInput(e.target.value);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isLoading) {
            e.preventDefault();
            handleSendMessage(userInput);
        }
    };

    // This is the correct way to handle closing the chat
    const handleCloseChat = () => {
        setIsChatOpen(false);
    };

    // Props for the child ChatWindow component
    const chatWindowProps = {
        messages,
        isLoading,
        suggestions,
        userInput,
        handleInputChange,
        handleKeyPress,
        handleSendMessage,
        handleResetChat,
        isMobile,
        isMaximized,
        setIsMaximized,
        chatContainerRef,
        handleCloseChat // Pass the function down as a prop
    };

    return (
        <ThemeProvider theme={theme}>
            <Fab color="primary" aria-label="chat" onClick={() => setIsChatOpen(prev => !prev)} title='Financial Advisor' sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200 }}>
                {isChatOpen ? <CloseIcon /> : <ChatIcon />}
            </Fab>

            {isMobile ? (
                // --- Mobile View: Full-screen Drawer ---
                <Drawer
                    anchor="bottom"
                    open={isChatOpen}
                    onClose={handleCloseChat} // Also good to have the drawer close on outside click
                    sx={{ '& .MuiDrawer-paper': { height: '100vh', width: '100vw' } }}
                >
                    <ChatWindow {...chatWindowProps} />
                </Drawer>
            ) : (
                // --- Desktop View: Minimized/Maximized Window ---
                <>
                    <Fade in={isChatOpen && !isMaximized}>
                        <Box sx={{ position: 'fixed', bottom: 96, right: 24, width: 400, height: 600, zIndex: 1100, borderRadius: '16px', boxShadow: 8, overflow: 'hidden' }}>
                            <ChatWindow {...chatWindowProps} />
                        </Box>
                    </Fade>
                    <Dialog open={isChatOpen && isMaximized} onClose={() => setIsMaximized(false)} fullWidth maxWidth="md" PaperProps={{ sx: { height: '85vh', borderRadius: '16px' } }}>
                        <DialogContent sx={{ p: 0, height: '100%' }}>
                            <ChatWindow {...chatWindowProps} />
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </ThemeProvider>
    );
}
